'use client'

import { type RefObject, useEffect } from 'react'

// viewport comes from clientWidth, which the browser rounds to a whole pixel,
// while the tracks come from getComputedStyle and keep sub-pixel precision —
// at non-integer browser zoom the two disagree by a fraction of a pixel. A
// tolerance absorbs that rounding so a grid that truly fits doesn't flip into
// a false overflow (and, via the ResizeObserver that re-measures after the
// runway shrinks the columns back under viewport, oscillate every frame).
const OVERFLOW_TOLERANCE_PX = 1

/**
 * Trailing space the grid needs for its last column to reach the label's edge.
 *
 * Zero unless the tracks already overflow: a runway on a grid that fits would
 * invent a scrollbar that is not otherwise there.
 */
export const computeCompareRunway = ({
  viewport,
  gutter,
  tracks,
}: {
  viewport: number
  gutter: number
  tracks: number[]
}) => {
  const [labelTrack, valueTrack] = tracks
  if (tracks.length < 2 || labelTrack === undefined || valueTrack === undefined)
    return 0
  if (
    tracks.some(Number.isNaN) ||
    Number.isNaN(viewport) ||
    Number.isNaN(gutter)
  )
    return 0

  const trackTotal = tracks.reduce((total, track) => total + track, 0)
  if (trackTotal + gutter <= viewport + OVERFLOW_TOLERANCE_PX) return 0

  return Math.max(0, viewport - gutter - labelTrack - valueTrack)
}

/**
 * Reserves trailing space so the last value column can scroll flush against
 * the sticky label column.
 *
 * The grid snaps each column to the label's right edge, but the scroll range
 * stops at the content's own width. When the distance left over is shorter
 * than one column, the scroll comes to rest past the final snap point with a
 * column straddling the label, and that column's tail shows beside the labels
 * — the defect the snapping exists to prevent. Making up the shortfall as
 * trailing space puts the final snap point back in reach.
 *
 * The shortfall is `viewport - gutter - label track - value track`, so it
 * depends on the container's width and CSS cannot express it: written as
 * `calc(100% - ...)` it is correct while the grid overflows, and forces a
 * scrollbar on wider screens where it does not overflow at all.
 */
export const useCompareColumnRunway = (
  scrollAreaRef: RefObject<HTMLDivElement | null>,
  gridRef: RefObject<HTMLDivElement | null>,
  columns: number,
  // The panel renders nothing while closed, so both refs are still null on the
  // render that opens it; without this the effect never re-runs to find them.
  isOpen: boolean
) => {
  useEffect(() => {
    const scrollArea = scrollAreaRef.current
    const grid = gridRef.current
    if (!scrollArea || !grid || typeof ResizeObserver === 'undefined') return

    const measure = () => {
      const tracks = getComputedStyle(grid)
        .gridTemplateColumns.split(' ')
        .map(Number.parseFloat)

      const runway = computeCompareRunway({
        viewport: scrollArea.clientWidth,
        gutter: Number.parseFloat(getComputedStyle(scrollArea).paddingRight),
        tracks,
      })

      // Writing unconditionally would resize the grid and re-enter this
      // observer on every callback.
      const next = `${Math.round(runway)}px`
      if (grid.style.getPropertyValue('--compare-runway') !== next) {
        grid.style.setProperty('--compare-runway', next)
      }
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(scrollArea)
    observer.observe(grid)
    return () => observer.disconnect()
  }, [scrollAreaRef, gridRef, columns, isOpen])
}
